import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "@augmodo/lebron-browser-client";
import type { ImageGallerySchema } from "../generated/types.gen";

interface ImageGalleryPayload extends Record<string, unknown> {
  image_ids: string[];
}

interface ImageGalleryResponse {
  images: ImageGallerySchema[];
}

async function fetchImageGallery(
  imageIds: string[],
): Promise<ImageGalleryResponse> {
  const response = await client.post({
    url: "/v1/images/gallery",
    body: { image_ids: imageIds },
    query: {
      page_size: 10000,
    },
  });

  if (response.error) {
    throw new Error(`Failed to fetch image gallery: ${response.error}`);
  }

  return response.data as ImageGalleryResponse;
}

export const imageGalleryQueryKey = (imageIds: string[] | undefined) => [
  "image-gallery",
  imageIds,
];

export const imageGalleryQueryOptions = (imageIds: string[] | undefined) => ({
  queryKey: imageGalleryQueryKey(imageIds),
  queryFn: () => fetchImageGallery(imageIds ?? []),
  enabled: !!imageIds && imageIds.length > 0,
  staleTime: 30 * 1000,
});

/**
 * React Query hook to fetch images by their IDs using the gallery endpoint
 */
export function useImageGallery() {
  return useMutation({
    mutationFn: (payload: ImageGalleryPayload) =>
      fetchImageGallery(payload.image_ids),
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      console.error("Image gallery fetch failed:", error);
    },
  });
}

/**
 * useQuery variant — cache is shared via the ["image-gallery", imageIds] key
 * so prefetched results can be reused by component renders.
 */
export function useImageGalleryQuery(imageIds: string[] | undefined) {
  return useQuery<ImageGalleryResponse>(imageGalleryQueryOptions(imageIds));
}
